import React, { useState, useEffect } from 'react';
import './JourneyRecommendations.css';
import { supabase } from '../supabaseClient';

const API_BASE_URL = process.env.REACT_APP_API_GATEWAY_URL;

async function getAuthToken() {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || null;
}

async function fetchOneJourney(token, userId, assessmentResults, budget, duration) {
  const response = await fetch(`${API_BASE_URL}/api/journey-generator`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ userId, assessmentResults, budget, duration }),
  });
  if (!response.ok) throw new Error(`Journey generator returned ${response.status}`);
  const body = await response.json();
  return body.journey;
}

// Question 4 in TransformationAssessment.js ("How long can you travel?") -
// its raw answer value was being saved to sessionStorage but never actually
// read here; every journey was generated (and priced - duration multiplies
// directly into the pricing formula) as if the user always picked "week".
const DURATION_DAYS = {
  weekend: 4,
  week: 7,
  two_weeks: 14,
  month_plus: 30,
};

function getRequestedDuration(assessmentResults) {
  const answer = assessmentResults?.answers?.[4];
  return DURATION_DAYS[answer] || 7;
}

async function fetchTours(token, destination) {
  const response = await fetch(`${API_BASE_URL}/api/tours-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ destination }),
  });
  if (!response.ok) throw new Error(`Tours search returned ${response.status}`);
  const body = await response.json();
  return body.tours || [];
}

function formatDuration(minutes) {
  if (!minutes) return null;
  const hours = Math.round(minutes / 60);
  return hours >= 24 ? `${Math.round(hours / 24)} day${hours >= 48 ? 's' : ''}` : `${hours} hr${hours !== 1 ? 's' : ''}`;
}

function toCardShape(journey) {
  return {
    id: journey.id,
    title: journey.title,
    destination: journey.overview.country,
    duration: `${journey.duration} days`,
    price: `$${journey.pricing.total.toLocaleString()}`,
    match: journey.match,
    description: journey.overview.theme,
    highlights: journey.activities,
    image: journey.photos?.[0] || null,
    images: journey.photos || [],
    pricing: journey.pricing,
  };
}

function JourneyRecommendations({ onNavigate }) {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [tours, setTours] = useState([]);
  const [toursLoading, setToursLoading] = useState(false);
  const [toursError, setToursError] = useState(null);

  useEffect(() => {
    loadJourneys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedJourney) return;

    let cancelled = false;
    (async () => {
      setToursLoading(true);
      setToursError(null);
      setTours([]);
      try {
        const token = await getAuthToken();
        if (!token) throw new Error('Not authenticated');
        const results = await fetchTours(token, selectedJourney.destination);
        if (!cancelled) setTours(results);
      } catch (error) {
        console.error('Error loading tours:', error);
        if (!cancelled) setToursError('Could not load tours right now.');
      } finally {
        if (!cancelled) setToursLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [selectedJourney]);

  const loadJourneys = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const stored = sessionStorage.getItem('assessmentResults');
      const assessmentResults = stored
        ? JSON.parse(stored)
        : { scores: { spiritual: 20, adventure: 20, cultural: 20, wellness: 20 }, answers: {} };

      const token = await getAuthToken();
      if (!token) throw new Error('Could not authenticate with the API');

      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user.id;

      // The backend generates one journey per call and picks among its top
      // matches for variety - firing more calls than we need to display
      // increases the odds of landing on 6 distinct real destinations rather
      // than showing repeats.
      const duration = getRequestedDuration(assessmentResults);
      const budgetRotation = ['base', 'budget', 'luxury', 'base', 'budget', 'luxury', 'base', 'luxury'];
      const results = await Promise.allSettled(
        budgetRotation.map((budget) => fetchOneJourney(token, userId, assessmentResults, budget, duration))
      );

      const seen = new Set();
      const unique = [];
      for (const r of results) {
        if (r.status !== 'fulfilled' || !r.value) continue;
        if (seen.has(r.value.destination)) continue;
        seen.add(r.value.destination);
        unique.push(toCardShape(r.value));
        if (unique.length >= 6) break;
      }

      if (unique.length === 0) throw new Error('No journeys returned');
      setJourneys(unique);
    } catch (error) {
      console.error('Error loading journeys:', error);
      setLoadError('Could not load your journeys right now - please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="recommendations-container">
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🧠</div>
          <h2>AI Transformation Engine</h2>
          <p>Analyzing your personality profile...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="recommendations-container">
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <p>{loadError}</p>
          <button className="btn-primary" onClick={loadJourneys}>Try Again</button>
        </div>
      </div>
    );
  }

  if (selectedJourney) {
    return (
      <div className="recommendations-container">
        <div className="modal-content">
          <button onClick={() => setSelectedJourney(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'white', fontSize: '2rem', cursor: 'pointer' }}>×</button>
          <h2>{selectedJourney.title}</h2>
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ color: '#ff6b6b', marginBottom: '1rem' }}>🎯 {selectedJourney.description}</h3>
            {selectedJourney.images.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: selectedJourney.images.length > 1 ? '2fr 1fr' : '1fr',
                gap: '0.75rem',
                marginBottom: '2rem',
              }}>
                {selectedJourney.images.slice(0, 2).map((src, idx) => (
                  <img
                    key={src}
                    src={src}
                    alt={`${selectedJourney.title} ${idx + 1}`}
                    style={{ width: '100%', height: '300px', borderRadius: '15px', objectFit: 'cover' }}
                  />
                ))}
              </div>
            )}

            <div style={{ background: 'rgba(255,255,255,0.1)', padding: '1.5rem', borderRadius: '15px', marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem', color: '#ff6b6b' }}>💰 Estimated Trip Cost</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>🏨 Accommodation</div>
                  <div style={{ fontSize: '1.5rem', color: '#ff6b6b' }}>${selectedJourney.pricing.accommodation.toLocaleString()}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>🎈 Activities</div>
                  <div style={{ fontSize: '1.5rem', color: '#ff6b6b' }}>${selectedJourney.pricing.activities.toLocaleString()}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>🍽️ Meals</div>
                  <div style={{ fontSize: '1.5rem', color: '#ff6b6b' }}>${selectedJourney.pricing.meals.toLocaleString()}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>🚐 Transfers</div>
                  <div style={{ fontSize: '1.5rem', color: '#ff6b6b' }}>${selectedJourney.pricing.transfers.toLocaleString()}</div>
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '1rem', background: 'rgba(255,107,107,0.2)', borderRadius: '10px', border: '2px solid #ff6b6b' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>🎯 Total (excl. flights)</div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff6b6b' }}>${selectedJourney.pricing.total.toLocaleString()}</div>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem' }}>✨ Highlights</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {selectedJourney.highlights.map((highlight, idx) => (
                  <span key={idx} style={{ background: 'rgba(255,255,255,0.1)', padding: '0.5rem 1rem', borderRadius: '15px', fontSize: '0.9rem' }}>
                    {highlight}
                  </span>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ marginBottom: '1rem' }}>🎫 Guided Tours & Activities</h4>
              {toursLoading && <p style={{ opacity: 0.8 }}>Finding real tours in {selectedJourney.destination}…</p>}
              {toursError && <p style={{ opacity: 0.8 }}>{toursError}</p>}
              {!toursLoading && !toursError && tours.length === 0 && (
                <p style={{ opacity: 0.8 }}>No tours found for {selectedJourney.destination} right now.</p>
              )}
              {tours.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
                  {tours.map(tour => (
                    <a
                      key={tour.id}
                      href={tour.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'block', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', overflow: 'hidden', textDecoration: 'none', color: 'white' }}
                    >
                      {tour.image && (
                        <img src={tour.image} alt={tour.title} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />
                      )}
                      <div style={{ padding: '0.75rem' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem', lineHeight: 1.3 }}>{tour.title}</div>
                        {tour.rating && (
                          <div style={{ fontSize: '0.75rem', opacity: 0.85, marginBottom: '0.3rem' }}>
                            ⭐ {tour.rating.toFixed(1)} ({tour.reviewCount})
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {formatDuration(tour.durationMinutes) && (
                            <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{formatDuration(tour.durationMinutes)}</span>
                          )}
                          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#ff6b6b' }}>
                            ${tour.price.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
              {tours.length > 0 && (
                <p style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.75rem' }}>
                  Tours are booked directly on Viator, our trusted activities partner.
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button onClick={() => setSelectedJourney(null)} style={{ flex: 1, padding: '1rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', color: 'white', borderRadius: '20px', cursor: 'pointer', fontWeight: '600' }}>
                ← Back
              </button>
              <button onClick={() => {
                sessionStorage.setItem('selectedJourney', selectedJourney.title);
                sessionStorage.setItem('selectedJourneyData', JSON.stringify(selectedJourney));
                window.location.hash = 'checkout';
              }} style={{ flex: 1, padding: '1rem', background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)', border: 'none', color: 'white', borderRadius: '20px', cursor: 'pointer', fontWeight: '600' }}>
                💳 Book Now - ${selectedJourney.pricing.total.toLocaleString()}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="recommendations-container">
      <div className="recommendations-header">
        <h1>🎯 Your Curated Journeys</h1>
        <p>Based on your travel profile, here are your perfect transformation journeys</p>
      </div>

      <div className="journeys-grid">
        {journeys.map(journey => (
          <div key={journey.id} className="journey-card" onClick={() => setSelectedJourney(journey)}>
            <div className="journey-image" style={journey.image ? { backgroundImage: `url(${journey.image})` } : { background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
              <div className="journey-overlay">
                <h3 className="journey-title">{journey.title}</h3>
                <div className="journey-aspiration">{journey.description}</div>
                <div className="match-badge">{journey.match}% Match</div>
              </div>
            </div>

            <div className="journey-content">
              <div className="journey-details">
                <div className="detail-item">
                  <span className="detail-label">📍 Destination:</span>
                  <span className="detail-value">{journey.destination}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">📅 Duration:</span>
                  <span className="detail-value">{journey.duration}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">💰 Price:</span>
                  <span className="detail-value">{journey.price}</span>
                </div>
              </div>

              <div className="highlights">
                <div className="highlights-label">Highlights:</div>
                <div className="highlights-list">
                  {journey.highlights.map((highlight, idx) => (
                    <span key={idx} className="highlight-tag">{highlight}</span>
                  ))}
                </div>
              </div>

              <button className="journey-btn" onClick={(e) => { e.stopPropagation(); setSelectedJourney(journey); }}>
                Learn More →
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="action-buttons">
        <button className="btn-secondary" onClick={() => onNavigate('landing')}>
          ← Back to Home
        </button>
      </div>
    </div>
  );
}

export default JourneyRecommendations;
